import { FileItem } from "@/components/files/FileItem.tsx";
import { CommentForm } from "@/components/comments/CommentForm.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useMentionsHook } from "@/hooks/use-mention.hook.ts";
import { ApiComment } from "@/types/comment.types.ts";
import {
  Box,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  HtmlViewer,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

interface CommentInlineProps {
  comment: ApiComment;
  onEdit: (text: string, fileUuids: string[]) => Promise<boolean>;
  onDelete: () => Promise<void>;
  useFiles?: boolean;
  useMentions?: boolean;
}

export function CommentInline({
  comment,
  onEdit,
  onDelete,
  useFiles = true,
  useMentions = true,
}: CommentInlineProps) {
  const { renderRelativeDate } = useFormatter();
  const [editIsOn, setEditIsOn] = useState(false);
  const { t } = useTranslation();
  const { replaceMentions } = useMentionsHook();

  const handleSave = useCallback(
    async (text: string, fileUuids: string[]) => {
      const success = await onEdit(text, fileUuids);
      if (success) setEditIsOn(false);
      return success;
    },
    [onEdit]
  );
  const isEdited = comment.datetime_created_at.getTime() !== comment.datetime_updated_at.getTime();

  return (
    <Stack items={"start"} gap={"sm"} className={"group"}>
      <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
        <EmployeeItem
          employee={comment.author}
          size={"sm"}
          direction={"horizontal"}
          variant={"secondary"}
          showPosition={false}
          caption={renderRelativeDate(comment.datetime_created_at)}
        />
        {isEdited && (
          <Tooltip delay={false}>
            <TooltipTrigger>
              <Text className={"text-secondary text-sm"}>({t("common:edited").toLowerCase()})</Text>
            </TooltipTrigger>
            <TooltipContent className={"max-w-40"}>
              {renderRelativeDate(comment.datetime_updated_at)}
            </TooltipContent>
          </Tooltip>
        )}
        {((comment.acl?.can_edit && !editIsOn) || comment.acl?.can_delete) && (
          <DropdownMenu
            className={"group-hover:opacity-100 opacity-0 transition duration-200"}
            trigger={<FontAwesomeIcon icon={"fa-light fa-ellipsis"} className={"cursor-pointer"} />}
          >
            {comment.acl?.can_edit && !editIsOn && (
              <DropdownMenuItem
                onClick={() => setEditIsOn(true)}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                title={t("common:edit")}
              />
            )}
            {comment.acl?.can_delete && (
              <DropdownMenuItem
                onClick={() => void onDelete()}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                variant={"menuDanger"}
                title={t("common:delete")}
              />
            )}
          </DropdownMenu>
        )}
      </Stack>

      {editIsOn ? (
        <Stack className={"w-full"}>
          <CommentForm
            entityId={comment.entity_id}
            entityType={comment.entity_type}
            comment={comment}
            isCollapsedOnInit={false}
            useFiles={useFiles}
            useMentions={useMentions}
            onSave={handleSave}
          />
        </Stack>
      ) : (
        <Stack gap={"sm"}>
          <Box className={"break-word"}>
            <HtmlViewer content={replaceMentions(comment.text, comment?.mentioned_employees)} />
          </Box>
          {comment.files.length > 0 && (
            <Stack gap={"sm"} className={"overflow-hidden"}>
              {comment.files.map((f) => (
                <Box className={"flex"} key={`comment-file-${f.id}`}>
                  <FileItem file={f} />
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
